import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { ListSkeleton } from "@/shared/components";
import { TrendingUp, TrendingDown, AlertTriangle, Minus } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const ChangeAlertSchema = z.object({
  changes: z.array(
    z.object({
      label: z.string(),
      previous: z.string(),
      current: z.string(),
      direction: z.enum(["up", "down", "neutral"]).optional(),
      severity: z.enum(["info", "warning", "critical"]).optional(),
      detail: z.string().optional(),
    }),
  ),
});

type ChangeAlertData = z.infer<typeof ChangeAlertSchema>;

/* ────────────────────── Helpers ────────────────────── */

const DIRECTION_ICON = {
  up: <TrendingUp className="size-3.5" />,
  down: <TrendingDown className="size-3.5" />,
  neutral: <Minus className="size-3.5" />,
} as const;

const DIRECTION_COLOR = {
  up: "text-emerald-600",
  down: "text-red-500",
  neutral: "text-text-tertiary",
} as const;

const SEVERITY_STYLE = {
  info: "border-l-blue-400",
  warning: "border-l-amber-400",
  critical: "border-l-red-500",
} as const;

/* ────────────────────── Component ────────────────────── */

export function ChangeAlert({
  widget,
  className,
}: AgentWidgetProps<ChangeAlertData>) {
  const { changes } = widget.data;

  if (changes.length === 0) return null;

  return (
    <div className={cn("space-y-1.5", className)}>
      {changes.map((change, idx) => {
        const dir = change.direction ?? "neutral";
        const sev = change.severity ?? "info";

        return (
          <div
            key={idx}
            className={cn(
              "flex items-center gap-3 rounded-lg border-l-[3px] bg-surface-secondary/30 px-3 py-2.5",
              SEVERITY_STYLE[sev],
            )}
          >
            {/* Direction indicator */}
            <div className={cn("shrink-0", DIRECTION_COLOR[dir])}>
              {DIRECTION_ICON[dir]}
            </div>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-medium text-text-primary">
                  {change.label}
                </span>
                {sev === "critical" && (
                  <AlertTriangle className="size-3 text-red-500" />
                )}
              </div>
              <div className="mt-0.5 flex items-center gap-1.5 text-[11px]">
                <span className="text-text-tertiary">{change.previous}</span>
                <span className="text-text-tertiary/60">→</span>
                <span className={cn("font-medium", DIRECTION_COLOR[dir])}>
                  {change.current}
                </span>
              </div>
              {change.detail && (
                <p className="mt-0.5 text-[10px] text-text-tertiary">
                  {change.detail}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function ChangeAlertSkeleton({ className }: { className?: string }) {
  return <ListSkeleton count={3} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const changeAlertWidget = defineWidget<ChangeAlertData>({
  type: WIDGET_TYPES.CHANGE_ALERT,
  label: "변동 알림",
  schema: ChangeAlertSchema,
  component: ChangeAlert,
  skeleton: ChangeAlertSkeleton,
  defaultLayout: "full",
  maxHeight: 240,
  aiHint: "순위 변동, 점수 변화, 이상 감지 등 모니터링 변동 알림 목록",
});

registerWidget(changeAlertWidget);
